import { motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { Effects, FadeMethod, MoveMethod } from "../../types";
import { DEFAULT_ANIMATION_DURATION_SECONDS } from "../../constants";
import { getMotionEasing } from "./easingMap";
import { BASE_PREVIEW_DELAY } from "./constants";
import { counterRotateOffset } from "./counterRotateOffset";

interface AnimState {
  x: number;
  y: number;
  instant: boolean;
}

interface LeaveToWrapperProps {
  children: React.ReactNode;
  effect: Effects;
  previewWidth: number;
  previewHeight: number;
  duration?: number;
  delay?: number;
  method?: MoveMethod | FadeMethod;
  rotation?: number;
}

export const LeaveToWrapper: React.FC<LeaveToWrapperProps> = ({
  children,
  effect,
  previewWidth,
  previewHeight,
  duration = DEFAULT_ANIMATION_DURATION_SECONDS,
  delay = 0,
  method,
  rotation = 0,
}) => {
  const [animState, setAnimState] = useState<AnimState>({
    x: 0,
    y: 0,
    instant: false,
  });

  const targetPos = useMemo(() => {
    const pos = { x: 0, y: 0 };
    if (effect === Effects.leaveToTop) pos.y = -previewHeight;
    if (effect === Effects.leaveToBottom) pos.y = previewHeight;
    if (effect === Effects.leaveToLeft) pos.x = -previewWidth;
    if (effect === Effects.leaveToRight) pos.x = previewWidth;
    // Keep the slide-out direction axis-aligned on screen even when the item is
    // rotated (the wrapper sits inside the item's rotation transform).
    return counterRotateOffset(pos, rotation);
  }, [effect, previewWidth, previewHeight, rotation]);

  useEffect(() => {
    const delayMs = delay * 1000;
    const durationMs = duration * 1000;

    const leaveTimer = setTimeout(
      () => {
        setAnimState({ ...targetPos, instant: false });
      },
      BASE_PREVIEW_DELAY * 1000 + delayMs
    );

    const resetTimer = setTimeout(
      () => {
        setAnimState({ x: 0, y: 0, instant: true });
      },
      BASE_PREVIEW_DELAY * 1000 + delayMs + durationMs + 500
    );

    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(resetTimer);
    };
  }, [targetPos, duration, delay]);

  return (
    <motion.div
      key={effect}
      initial={{ x: 0, y: 0 }}
      animate={{ x: animState.x, y: animState.y }}
      transition={{
        duration: animState.instant ? 0 : duration,
        ease: animState.instant ? undefined : getMotionEasing(method),
      }}
      style={{ width: "100%", height: "100%" }}
    >
      {children}
    </motion.div>
  );
};
